import { Award, Calendar, CheckCircle2, Loader2, PauseCircle, TrendingUp } from 'lucide-react';
import React from 'react';

import { supabase } from '../../lib/supabase';
import { useStudentProgress, type StudentProgressRow } from '../../hooks/belts/useRankViews';
import { useQuery } from '@tanstack/react-query';

/**
 * Student-facing rank view (#750). Read-only. Lists every discipline the
 * student has a rank in, with current rank, progress toward next, and the
 * on-hold history per discipline.
 */
export default function MyRankPage() {
  const me = useMyUserId();
  const progress = useStudentProgress(me.data);

  const active = (progress.data ?? []).filter((r) => r.state === 'active');
  const onHold = (progress.data ?? []).filter((r) => r.state === 'on_hold');

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#EBFFEE] flex items-center gap-2">
          <Award className="w-6 h-6 text-emerald-400" /> My rank
        </h1>
        <p className="text-sm text-white/60 mt-1">
          Your current rank in each discipline and how close you are to the next
          one. Promotions are at your instructor&rsquo;s discretion &mdash;
          these are advisory.
        </p>
      </div>

      {progress.isLoading && (
        <div className="flex items-center gap-2 text-white/60">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {!progress.isLoading && (progress.data?.length ?? 0) === 0 && (
        <div className="text-sm text-white/60 rounded-md border border-white/10 bg-white/[0.02] p-6 text-center">
          You don&rsquo;t have a rank set up yet. Ask your instructor.
        </div>
      )}

      {active.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-[#EBFFEE]">Active discipline</h2>
          {active.map((row) => <RankCard key={row.student_rank_id} row={row} />)}
        </section>
      )}

      {onHold.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-[#EBFFEE]">On hold</h2>
          <p className="text-xs text-white/50">
            Time pauses on these while you focus on your active discipline.
          </p>
          {onHold.map((row) => <RankCard key={row.student_rank_id} row={row} compact />)}
        </section>
      )}
    </div>
  );
}

function RankCard({ row, compact }: { row: StudentProgressRow; compact?: boolean }) {
  const isActive = row.state === 'active';
  const timeProgressPct = row.next_time_threshold_days
    ? Math.min(100, Math.round((row.days_at_rank / row.next_time_threshold_days) * 100))
    : null;
  const attendanceProgressPct = row.next_attendance_threshold
    ? Math.min(100, Math.round((row.attendance_since / row.next_attendance_threshold) * 100))
    : null;
  const stripeIndex = row.current_rank_order;
  const totalRanks = row.total_ranks_in_profile;

  return (
    <div className={`rounded-lg border ${isActive ? 'border-emerald-500/30 bg-emerald-500/[0.04]' : 'border-amber-500/20 bg-amber-500/[0.03]'} p-4 space-y-3`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-white/50">{row.sport_name}</div>
          <div className="text-xl font-semibold text-[#EBFFEE] mt-1">
            {row.current_rank_name}
          </div>
          {row.profile_name && (
            <div className="text-xs text-white/50 mt-0.5">{row.profile_name}</div>
          )}
        </div>
        {isActive ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-300 bg-emerald-500/15 border border-emerald-500/40 rounded px-2 py-0.5">
            <CheckCircle2 className="w-3 h-3" /> Active
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-amber-300 bg-amber-500/15 border border-amber-500/40 rounded px-2 py-0.5">
            <PauseCircle className="w-3 h-3" /> On hold
            {row.hold_started_at && (
              <> · since {new Date(row.hold_started_at).toLocaleDateString()}</>
            )}
          </span>
        )}
      </div>

      {/* Stripe-style step indicator across the entire profile's rank ladder. */}
      {totalRanks > 0 && (
        <div className="flex items-center gap-0.5">
          {Array.from({ length: totalRanks }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded ${i <= stripeIndex ? (isActive ? 'bg-emerald-400' : 'bg-amber-400/70') : 'bg-white/10'}`}
              title={`Rank ${i + 1} of ${totalRanks}`}
            />
          ))}
        </div>
      )}

      {!compact && (
        <>
          {row.next_rank_name ? (
            <div className="space-y-2 pt-1">
              <div className="text-xs text-white/60 flex items-center gap-1">
                <TrendingUp className="w-3.5 h-3.5" />
                Next: <span className="text-emerald-300 ml-1">{row.next_rank_name}</span>
              </div>
              {timeProgressPct !== null && (
                <ProgressBar
                  label="Time at rank"
                  current={`${row.days_at_rank}d`}
                  target={`${row.next_time_threshold_days}d`}
                  pct={timeProgressPct}
                  icon={<Calendar className="w-3.5 h-3.5 text-white/50" />}
                />
              )}
              {attendanceProgressPct !== null && (
                <ProgressBar
                  label="Classes since promotion"
                  current={String(row.attendance_since)}
                  target={String(row.next_attendance_threshold)}
                  pct={attendanceProgressPct}
                  icon={<CheckCircle2 className="w-3.5 h-3.5 text-white/50" />}
                />
              )}
              {timeProgressPct === null && attendanceProgressPct === null && (
                <div className="text-xs text-white/50">
                  No thresholds set for the next rank &mdash; promotion is at instructor&rsquo;s discretion.
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-white/60 pt-1 flex items-center gap-1">
              <Award className="w-3.5 h-3.5 text-emerald-400" />
              Top of the ladder. No higher rank in this profile.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProgressBar({
  label,
  current,
  target,
  pct,
  icon,
}: {
  label: string;
  current: string;
  target: string;
  pct: number;
  icon: React.ReactNode;
}) {
  const reached = pct >= 100;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs text-white/70">
        <span className="flex items-center gap-1.5">
          {icon} {label}
        </span>
        <span className={reached ? 'text-emerald-300' : 'text-white/60'}>
          {current} / {target}
        </span>
      </div>
      <div className="h-1.5 bg-white/5 rounded overflow-hidden">
        <div
          className={`h-full ${reached ? 'bg-emerald-400' : 'bg-emerald-400/60'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Resolves the current user's id from the active session.

function useMyUserId() {
  return useQuery({
    queryKey: ['auth-uid'],
    queryFn: async () => {
      const { data } = await supabase.auth.getUser();
      return data.user?.id;
    },
    staleTime: 5 * 60_000,
  });
}
